'use client'

import { useEffect, useRef, useState, useCallback } from 'react'
import { useRouter } from 'next/navigation'
import { createPortal } from 'react-dom'
import {
  Play, Pause, SkipBack, SkipForward, Shuffle, Volume2, VolumeX, ListMusic, X,
  GripVertical, Search as SearchIcon, QrCode as QrCodeIcon,
} from 'lucide-react'
import { fmtTime, usePlayer, usePlayerPosition } from '@/lib/player-context'
import Marquee from './Marquee'
import BaffleConnect from './BaffleConnect'
import { useArtwork } from '@/lib/artwork'
import { useCoverOverride } from '@/lib/cover-overrides'
import { useMiniSearch } from './MiniSearchBridge'

/**
 * Mini-lecteur flottant draggable, rendu DANS la page (pas via Document PiP).
 *
 * — Aucun bandeau Chrome / Windows
 * — Marche dans le navigateur ET dans l'app Electron
 * — Marche sur Firefox / Safari / mobile
 * — Position sauvegardée en localStorage
 *
 * L'API publique reste compatible avec l'ancien Document-PiP : `useMiniPlayerPip()`
 * expose `open / close / isOpen / pipWin`. `pipWin` est null en permanence (legacy).
 */

const STORAGE_KEY = 'blc.minipip.v2'
type Pos = { x: number; y: number }

// API blcDesktop exposée par le preload Electron.
// Quand elle est dispo, le mini-lecteur s'ouvre dans une vraie BrowserWindow
// frameless détachée (toujours au-dessus, sans bordure) — sinon on retombe
// sur le floating overlay in-page.
type DesktopBridge = {
  isDesktop?: boolean
  openPip?: () => void
  closePip?: () => void
  pushPipState?: (s: any) => void
  onPipAction?: (cb: (a: any) => void) => () => void
  onPipClosed?: (cb: () => void) => () => void
}
function bridge(): DesktopBridge | null {
  if (typeof window === 'undefined') return null
  return ((window as any).blcDesktop as DesktopBridge) || null
}
function hasElectronPip(): boolean {
  const b = bridge()
  return !!(b?.isDesktop && b.openPip && b.closePip)
}

function isTrustedJarvisOrigin(origin: string): boolean {
  try {
    const url = new URL(origin)
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return false
    const host = url.hostname.toLowerCase()
    return host === 'localhost' || host === '127.0.0.1' || host === '::1' ||
      /^10\./.test(host) || /^192\.168\./.test(host) ||
      /^172\.(1[6-9]|2\d|3[01])\./.test(host) || host.includes('jarvis')
  } catch {
    return false
  }
}

let _open = false
const _listeners = new Set<(open: boolean) => void>()
function _setOpen(v: boolean) {
  _open = v
  _listeners.forEach(fn => fn(v))
}

export function useMiniPlayerPip() {
  const [isOpen, setOpen] = useState<boolean>(_open)
  useEffect(() => {
    _listeners.add(setOpen)
    return () => { _listeners.delete(setOpen) }
  }, [])

  // Quand on est en Electron, on synchronise _open avec onPipClosed (X clické dans la PiP)
  useEffect(() => {
    const b = bridge()
    if (!b?.onPipClosed) return
    const off = b.onPipClosed(() => _setOpen(false))
    return () => { off?.() }
  }, [])

  const open = useCallback(() => {
    if (hasElectronPip()) {
      try { bridge()!.openPip!() } catch {}
    }
    _setOpen(true)
  }, [])
  const close = useCallback(() => {
    if (hasElectronPip()) {
      try { bridge()!.closePip!() } catch {}
    }
    _setOpen(false)
  }, [])

  return { open, close, pipWin: null as Window | null, isOpen }
}

/* eslint-disable @typescript-eslint/no-unused-vars */
export default function MiniPlayerPipPortal({
  pipWin: _pipWin, onClose,
}: { pipWin: Window | null; onClose: () => void }) {
  const ctx = usePlayer()
  const position = usePlayerPosition()
  const router = useRouter()
  const { url: artworkUrl } = useArtwork(ctx.current)
  // Pochette perso uploadée = priorité absolue (registre global par videoId,
  // ou thumbnail en data URL).
  const overrideCover = useCoverOverride(ctx.current?.videoId)
  const customCover = overrideCover || (ctx.current?.thumbnail && ctx.current.thumbnail.startsWith('data:') ? ctx.current.thumbnail : null)
  const miniSearch = useMiniSearch()

  // Mute toggle
  const lastVol = useRef<number>(ctx.volume || 70)
  useEffect(() => { if (ctx.volume > 0) lastVol.current = ctx.volume }, [ctx.volume])
  const muted = ctx.volume === 0
  const toggleMute = () => ctx.setVolume(muted ? (lastVol.current || 70) : 0)

  // Pont vers le widget BLC Music de Jarvis. Le lecteur reste la source de
  // vérité : Jarvis reçoit le morceau, la vraie pochette et la progression,
  // puis renvoie ici les commandes lecture/pause/précédent/suivant.
  const jarvisOriginRef = useRef<string | null>(null)
  const jarvisLastPushRef = useRef(0)
  const jarvisStateRef = useRef<any>(null)
  const openJarvisSearch = useCallback((query?: string) => {
    const q = String(query || '').trim()
    router.push(q ? `/app/search?q=${encodeURIComponent(q)}` : '/app/search')
  }, [router])
  const jarvisControlsRef = useRef({
    playing: ctx.playing,
    toggle: ctx.toggle,
    next: ctx.next,
    prev: ctx.prev,
    seek: ctx.seek,
    search: openJarvisSearch,
  })
  jarvisStateRef.current = {
    title: ctx.current?.title || 'Aucune piste',
    artist: ctx.current?.artist || '',
    // Titre musical : jamais l'image de la vidéo (seulement pour une vraie vidéo).
    artworkUrl: customCover || artworkUrl || (ctx.current?.isVideo ? ctx.current.thumbnail : null) || null,
    thumbnail: ctx.current?.thumbnail || null,
    position: position || 0,
    duration: ctx.duration || 0,
    playing: !!ctx.playing,
  }
  jarvisControlsRef.current = {
    playing: ctx.playing,
    toggle: ctx.toggle,
    next: ctx.next,
    prev: ctx.prev,
    seek: ctx.seek,
    search: openJarvisSearch,
  }

  const pushJarvisState = useCallback((force = false) => {
    if (typeof window === 'undefined' || window.parent === window) return
    const origin = jarvisOriginRef.current
    if (!origin || !isTrustedJarvisOrigin(origin)) return
    const now = Date.now()
    if (!force && now - jarvisLastPushRef.current < 700) return
    jarvisLastPushRef.current = now
    try {
      window.parent.postMessage({
        type: 'blc-music:state',
        version: 1,
        state: jarvisStateRef.current,
      }, origin)
    } catch {}
  }, [])

  useEffect(() => {
    if (typeof window !== 'undefined' && window.parent !== window) {
      try { window.parent.postMessage({ type: 'blc-music:ready', version: 1 }, '*') } catch {}
    }
    try {
      const referrerOrigin = document.referrer ? new URL(document.referrer).origin : ''
      if (isTrustedJarvisOrigin(referrerOrigin)) jarvisOriginRef.current = referrerOrigin
    } catch {}
    pushJarvisState(true)
  }, [pushJarvisState])

  useEffect(() => {
    pushJarvisState(false)
  }, [
    pushJarvisState,
    ctx.current?.videoId, ctx.current?.title, ctx.current?.artist, ctx.current?.thumbnail,
    artworkUrl, customCover, position, ctx.duration, ctx.playing,
  ])

  useEffect(() => {
    const heartbeat = window.setInterval(() => pushJarvisState(true), 3000)
    return () => window.clearInterval(heartbeat)
  }, [pushJarvisState])

  useEffect(() => {
    const onJarvisMessage = (event: MessageEvent) => {
      if (event.source !== window.parent || !isTrustedJarvisOrigin(event.origin)) return
      const message = event.data
      if (!message || typeof message !== 'object' || !String(message.type || '').startsWith('blc-music:')) return
      jarvisOriginRef.current = event.origin
      if (message.type === 'blc-music:hello' || message.type === 'blc-music:request-state') {
        pushJarvisState(true)
        return
      }
      if (message.type !== 'blc-music:control') return
      const controls = jarvisControlsRef.current
      switch (message.action) {
        case 'playpause': controls.toggle(); break
        case 'play': if (!controls.playing) controls.toggle(); break
        case 'pause': if (controls.playing) controls.toggle(); break
        case 'next': controls.next(); break
        case 'prev': controls.prev(); break
        case 'seek': if (typeof message.seconds === 'number') controls.seek(message.seconds); break
        case 'search': controls.search(typeof message.query === 'string' ? message.query : ''); break
      }
      window.setTimeout(() => pushJarvisState(true), 80)
    }
    window.addEventListener('message', onJarvisMessage)
    return () => window.removeEventListener('message', onJarvisMessage)
  }, [pushJarvisState])

  // ============== Mode Electron PiP : on push l'état + écoute les actions =================
  // En Electron, on ne rend PAS le floating overlay (la fenêtre PiP détachée fait l'UI).
  const electronMode = hasElectronPip() && _open
  useEffect(() => {
    if (!electronMode) return
    const b = bridge()
    // Push initial + à chaque change
    try {
      b?.pushPipState?.({
        title:    ctx.current?.title || 'Aucune piste',
        artist:   ctx.current?.artist || '',
        artworkUrl: customCover || artworkUrl,
        // Titre musical : pas d'image vidéo en repli dans la PiP (seulement pour une vraie vidéo).
        thumbnail: ctx.current?.isVideo ? (ctx.current.thumbnail || null) : null,
        position:  position || 0,
        duration:  ctx.duration || 0,
        playing:   !!ctx.playing,
        volume:    ctx.volume,
        repeat:    ctx.repeat,
        shuffle:   ctx.shuffle,
      })
    } catch {}
  }, [
    electronMode,
    ctx.current?.videoId, ctx.current?.title, ctx.current?.artist, ctx.current?.thumbnail,
    artworkUrl, position, ctx.duration, ctx.playing, ctx.volume,
    ctx.repeat, ctx.shuffle,
  ])

  // Écoute les clics venant de la fenêtre PiP
  useEffect(() => {
    if (!electronMode) return
    const b = bridge()
    if (!b?.onPipAction) return
    const off = b.onPipAction((a: any) => {
      try {
        switch (a?.type) {
          case 'toggle':  ctx.toggle(); break
          case 'next':    ctx.next();   break
          case 'prev':    ctx.prev();   break
          case 'seek':    if (typeof a.sec === 'number') ctx.seek(a.sec); break
          case 'volume':  if (typeof a.v   === 'number') ctx.setVolume(a.v); break
          case 'mute':    ctx.setVolume(0); break
          case 'unmute':  ctx.setVolume(lastVol.current || 70); break
          case 'shuffle': ctx.toggleShuffle(); break
          case 'repeat':  ctx.cycleRepeat(); break
          case 'queue':   /* TODO: ouvrir la file d'attente */ break
          case 'search':  miniSearch.open(); break
          case 'close':   onClose(); break
        }
      } catch {}
    })
    return () => { off?.() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [electronMode])

  // ⚠️ Position draggable + persistée — DOIT être appelée AVANT le return early
  // sinon on viole les Rules of Hooks (les hooks suivants ne seraient pas
  // appelés à chaque render → React error #300 / #310).
  const [pos, setPos] = useState<Pos>(() => {
    if (typeof window === 'undefined') return { x: 24, y: 100 }
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (raw) {
        const p = JSON.parse(raw)
        if (typeof p?.x === 'number' && typeof p?.y === 'number') return p
      }
    } catch {}
    if (typeof window !== 'undefined') {
      return { x: Math.max(20, window.innerWidth - 520), y: Math.max(20, window.innerHeight - 260) }
    }
    return { x: 24, y: 100 }
  })

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(pos)) } catch {}
  }, [pos])

  useEffect(() => {
    const onResize = () => {
      setPos(p => ({
        x: Math.max(8, Math.min(window.innerWidth - 200, p.x)),
        y: Math.max(8, Math.min(window.innerHeight - 60, p.y)),
      }))
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const dragRef = useRef<{ dx: number; dy: number } | null>(null)

  // En mode Electron, la BrowserWindow PiP frameless s'occupe de l'UI :
  // on ne rend rien ici. (Le return est APRÈS tous les hooks pour respecter
  // les Rules of Hooks.)
  if (electronMode) return null

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement
    if (target.closest('button, input, a')) return
    dragRef.current = { dx: e.clientX - pos.x, dy: e.clientY - pos.y }
    ;(e.target as HTMLElement).setPointerCapture?.(e.pointerId)
  }
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return
    setPos({
      x: Math.max(8, Math.min(window.innerWidth - 80,  e.clientX - dragRef.current.dx)),
      y: Math.max(8, Math.min(window.innerHeight - 40, e.clientY - dragRef.current.dy)),
    })
  }
  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    dragRef.current = null
    ;(e.target as HTMLElement).releasePointerCapture?.(e.pointerId)
  }

  if (!_open || typeof document === 'undefined') return null

  const t = ctx.current
  const pct = ctx.duration > 0 ? (position / ctx.duration) * 100 : 0

  return createPortal(
    <div
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      style={{
        position: 'fixed',
        left: pos.x, top: pos.y,
        width: 480, maxWidth: 'calc(100vw - 16px)',
        zIndex: 60,
        display: 'flex', alignItems: 'stretch',
        background: 'linear-gradient(135deg, rgba(26,22,66,0.97) 0%, rgba(12,14,40,0.97) 100%)',
        backdropFilter: 'blur(18px)',
        WebkitBackdropFilter: 'blur(18px)',
        padding: 10, gap: 12,
        borderRadius: 18,
        border: '1px solid rgba(255,255,255,0.08)',
        boxShadow: '0 18px 48px rgba(0,0,0,0.6), 0 0 32px rgb(var(--a2)/0.28)',
        boxSizing: 'border-box',
        cursor: dragRef.current ? 'grabbing' : 'grab',
        userSelect: 'none',
      }}
    >
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'rgba(255,255,255,0.35)', flexShrink: 0,
      }}>
        <GripVertical size={14} />
      </div>

      <div style={{
        width: 90, height: 90, borderRadius: 14,
        overflow: 'hidden', flexShrink: 0,
        background: 'linear-gradient(135deg, rgb(var(--adeep)), rgb(var(--spanel)))',
        boxShadow: '0 8px 22px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.08)',
      }}>
        {/* Titre musical : VRAIE pochette uniquement (jamais l'image de la vidéo, sauf pour une vidéo). */}
        {(customCover || artworkUrl || (t?.isVideo ? t.thumbnail : undefined)) && (
          <img
            src={customCover || artworkUrl || (t?.isVideo ? t.thumbnail : undefined)}
            alt=""
            draggable={false}
            style={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }}
          />
        )}
      </div>

      <div style={{
        flex: 1, minWidth: 0,
        display: 'flex', flexDirection: 'column',
        gap: 6,
      }}>
        {/* Titre + artiste(s) — sur leur PROPRE ligne, et DÉFILANTS (marquee) si
            trop longs (titres à rallonge / plusieurs artistes). */}
        <div style={{ minWidth: 0 }}>
          <Marquee className="text-[13px] font-bold text-white leading-tight" speed={38}>
            {t?.title || 'Aucune piste'}
          </Marquee>
          <Marquee className="text-[11px] text-white/60 leading-tight mt-0.5" speed={32}>
            {t?.artist || '—'}
          </Marquee>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ flex: 1 }} />

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={ctx.toggleShuffle} title={ctx.shuffle ? 'Aléatoire activé' : 'Aléatoire'}
              style={{ ...iconBtn, color: ctx.shuffle ? 'rgb(var(--a4))' : 'rgba(255,255,255,0.7)' }}>
              <Shuffle size={14} />
            </button>
            <button onClick={ctx.prev} title="Précédent" style={iconBtn}>
              <SkipBack size={16} fill="white" />
            </button>
            <button
              onClick={ctx.toggle}
              title={ctx.playing ? 'Pause' : 'Lecture'}
              style={{
                width: 40, height: 40, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'linear-gradient(135deg, rgb(var(--a2)), rgb(var(--a1)))',
                border: 'none', color: 'white', cursor: 'pointer',
                boxShadow: '0 6px 18px rgb(var(--a2)/0.55), 0 0 18px rgb(var(--a2)/0.45)',
              }}>
              {ctx.playing
                ? <Pause size={16} fill="white" />
                : <Play size={16} fill="white" style={{ marginLeft: 1 }} />}
            </button>
            <button onClick={ctx.next} title="Suivant" style={iconBtn}>
              <SkipForward size={16} fill="white" />
            </button>
          </div>

          <div style={{ flex: 1 }} />

          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button onClick={toggleMute} title={muted ? 'Activer le son' : 'Couper le son'}
              style={{ ...iconBtn, color: muted ? 'rgb(var(--a4))' : 'rgba(255,255,255,0.85)' }}>
              {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
            </button>
            <input
              type="range"
              min={0} max={100}
              value={ctx.volume}
              aria-label="Volume"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              onInput={(e) => ctx.setVolume(Number(e.currentTarget.value))}
              onChange={(e) => ctx.setVolume(Number(e.target.value))}
              title={`Volume : ${ctx.volume}%`}
              style={{
                width: 64, height: 3, appearance: 'none', borderRadius: 999,
                background: `linear-gradient(90deg, rgb(var(--a1)) 0%, rgb(var(--a3)) ${ctx.volume}%, rgba(255,255,255,0.14) ${ctx.volume}%, rgba(255,255,255,0.14) 100%)`,
                outline: 'none', cursor: 'pointer', touchAction: 'none',
              }}
            />
            {/* QR : connecter le téléphone → envoyer son son sur cet appareil */}
            <BaffleConnect style={{ ...iconBtn, color: '#22D3EE' }}>
              <QrCodeIcon size={15} />
            </BaffleConnect>
            {/* Recherche rapide — ouvre la mini-fenêtre /search-window */}
            <button
              onClick={() => miniSearch.open()}
              title="Recherche rapide (mini-fenêtre)"
              style={{ ...iconBtn, color: 'rgba(255,255,255,0.85)' }}
            >
              <SearchIcon size={14} />
            </button>
            <button title="File d'attente" style={iconBtn}>
              <ListMusic size={14} />
            </button>
            <button onClick={onClose} title="Fermer le mini-lecteur"
              style={{ ...iconBtn, color: 'rgba(255,255,255,0.55)' }}>
              <X size={14} />
            </button>
          </div>
        </div>

        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          fontSize: 10, color: 'rgba(255,255,255,0.55)',
          fontVariantNumeric: 'tabular-nums',
        }}>
          <span style={{ minWidth: 30 }}>{fmtTime(position)}</span>
          <input
            type="range" min={0} max={Math.max(1, Math.floor(ctx.duration))}
            value={Math.floor(position)}
            onChange={(e) => ctx.seek(Number(e.target.value))}
            style={{
              flex: 1, height: 3, appearance: 'none', borderRadius: 999,
              background: `linear-gradient(90deg, rgb(var(--a1)) 0%, rgb(var(--a3)) ${pct}%, rgba(255,255,255,0.14) ${pct}%, rgba(255,255,255,0.14) 100%)`,
              outline: 'none',
            }}
          />
          <span style={{ minWidth: 30, textAlign: 'right' }}>{fmtTime(ctx.duration)}</span>
        </div>
      </div>
    </div>,
    document.body,
  )
}

const iconBtn: React.CSSProperties = {
  width: 26, height: 26,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'transparent',
  border: 'none', borderRadius: 6,
  color: 'rgba(255,255,255,0.85)', cursor: 'pointer',
  padding: 0,
}
